<template>
  <BaseResponseComponent :response="response">
    <template #default="{ response: responseData }">
      <div class="general-chat-response">
        <!-- Main message content -->
        <div class="message-text" v-html="formatMessage(responseData.message)"></div>
        
        <!-- Suggested actions -->
        <SuggestedActions 
          v-if="responseData.suggested_actions && responseData.suggested_actions.length > 0"
          :actions="responseData.suggested_actions"
          @action-click="handleActionClick"
        />
        
        <!-- Context hints for debugging (only in development) -->
        <div v-if="showDebugInfo && responseData.context_hints" class="debug-info">
          <details>
            <summary>Debug Info</summary>
            <div class="context-hints">
              <strong>Context Hints:</strong> {{ responseData.context_hints.join(', ') }}
            </div>
          </details>
        </div>
      </div>
    </template>
  </BaseResponseComponent>
</template>

<script>
import BaseResponseComponent from './BaseResponseComponent.vue';
import SuggestedActions from './SuggestedActions.vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Configure marked options for security
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
  headerIds: false, // Disable header IDs for security
  mangle: false, // Don't mangle email addresses
});

export default {
  name: 'GeneralChatResponse',
  components: {
    BaseResponseComponent,
    SuggestedActions
  },
  props: {
    response: {
      type: Object,
      required: true
    },
    showDebugInfo: {
      type: Boolean,
      default: false
    }
  },
  emits: ['action-click'],

  mounted() {
    // Configure marked options for security
    marked.setOptions({
      breaks: true, // Convert \n to <br>
      gfm: true, // GitHub Flavored Markdown
      headerIds: false, // Disable header IDs for security
      mangle: false, // Don't mangle email addresses
    });

    // Configure DOMPurify to add target="_blank" and rel="noopener noreferrer" to links
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
  },

  methods: {
    formatMessage(text) {
      if (typeof text !== 'string') return '';
      // Basic security: escape HTML, then replace newlines with <br>
        // Parse markdown to HTML
        const rawHtml = marked.parse(text);
        
        // Sanitize HTML to prevent XSS attacks
        const cleanHtml = DOMPurify.sanitize(rawHtml, {
          ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
            'blockquote', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3',
            'h4', 'h5', 'h6', 'hr', 'table', 'thead', 'tbody', 'tr',
            'th', 'td', 'img'
          ],
          ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel'],
          ALLOW_DATA_ATTR: false,
          USE_PROFILES: { html: true }
        });
        
        return cleanHtml;
    },
    
    handleActionClick(action) {
      this.$emit('action-click', action);
    }
  }
};
</script>

<style scoped>
.general-chat-response {
  width: 100%;
}

.message-text {
  font-size: 0.95rem;
  line-height: 1.5;
  color: #333;
  margin-bottom: 8px;
}

.debug-info {
  margin-top: 12px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #6c757d;
}

.debug-info details {
  cursor: pointer;
}

.debug-info summary {
  font-weight: 500;
  margin-bottom: 4px;
}

.context-hints {
  font-family: monospace;
  font-size: 0.75rem;
  background-color: #e9ecef;
  padding: 4px 6px;
  border-radius: 3px;
  margin-top: 4px;
}

/* Markdown content styling */
.message-text :deep(p) {
  margin-bottom: 0.5rem;
}

.message-text :deep(p:last-child) {
  margin-bottom: 0;
}

.message-text :deep(h1),
.message-text :deep(h2),
.message-text :deep(h3),
.message-text :deep(h4),
.message-text :deep(h5),
.message-text :deep(h6) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.message-text :deep(h1) { font-size: 1.5rem; }
.message-text :deep(h2) { font-size: 1.3rem; }
.message-text :deep(h3) { font-size: 1.1rem; }
.message-text :deep(h4) { font-size: 1rem; }
.message-text :deep(h5) { font-size: 0.9rem; }
.message-text :deep(h6) { font-size: 0.85rem; }

.message-text :deep(code) {
  background-color: #f4f4f4;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
}

.message-text :deep(pre) {
  background-color: #f4f4f4;
  padding: 1rem;
  border-radius: 5px;
  overflow-x: auto;
  margin: 0.5rem 0;
}

.message-text :deep(pre code) {
  background-color: transparent;
  padding: 0;
}

.message-text :deep(blockquote) {
  border-left: 4px solid #ddd;
  padding-left: 1rem;
  margin: 0.5rem 0;
  color: #666;
}

.message-text :deep(ul),
.message-text :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.message-text :deep(li) {
  margin: 0.25rem 0;
}

.message-text :deep(a) {
  color: #007bff;
  text-decoration: none;
}

.message-text :deep(a:hover) {
  text-decoration: underline;
}

.message-text :deep(hr) {
  margin: 1rem 0;
  border: none;
  border-top: 1px solid #ddd;
}

.message-text :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5rem 0;
}

.message-text :deep(th),
.message-text :deep(td) {
  border: 1px solid #ddd;
  padding: 0.5rem;
  text-align: left;
}

.message-text :deep(th) {
  background-color: #f4f4f4;
  font-weight: 600;
}

.message-text :deep(img) {
  max-width: 100%;
  height: auto;
}
  
</style>